import { Fragment, useState } from "react"
import PropTypes from "prop-types"
import AccountCenterSearch from "components/admin/account_center_search/account_center_search"
import Icon from "components/planning_center_icon"
import { checkRestrictions } from "components/admin/restrictions"
import { Select } from "components/ui"
import { api } from "utils/api"
import { isBlank, isPresent, arrayToSentence, unescape } from "utils/helpers"

const checkRestrictionMessage = (attendee, type) => {
  if (isBlank(type) || isBlank(attendee)) {
    return ""
  }

  const atMaxCapacity = attendee.hasReachedCapacity || type.at_maximum_capacity

  const restrictions = checkRestrictions(attendee, type)
    .filter(r => r.isRestricted)
    .map(r => r.type)

  let statusMessage = ""

  if (restrictions.length === 0 && !atMaxCapacity) {
    return statusMessage
  }

  const restrictionMessage = `${attendee.name} doesn't meet the ${arrayToSentence(
    restrictions
  )} restrictions for ${atMaxCapacity ? "this selection" : type.name}.`

  if (atMaxCapacity) {
    statusMessage = `${type.name} is at maximum capacity${
      restrictions.length > 0 ? " and " + restrictionMessage : "."
    } Adding this attendee will cause capacity to automatically increase by 1.`
  } else {
    statusMessage = restrictionMessage
  }

  return statusMessage
}

const shapeAttendee = obj => ({
  name: unescape(obj.name),
  firstName: unescape(obj.firstName),
  lastName: unescape(obj.lastName),
  age: obj.age,
  ageInMonths: obj.ageInMonths,
  birthdate: obj.birthdate,
  gender: obj.gender,
  grade: obj.grade,
  avatarUrl: obj.avatarUrl,
  accountCenterId: obj.accountCenterId
})

const AddAttendees = ({
  attendeeTypes,
  onAttendeesChange,
  accountsPeoplePath,
  existingAttendees = [],
  displayWarnings = true,
  hideCost = false
}) => {
  const [attendees, setAttendees] = useState(
    existingAttendees.map(a => ({
      id: a.personId,
      attendeeId: a.id,
      personId: a.personId,
      isCanceled: a.isCanceled,
      attendeeTypeId: a.attendeeTypeId,
      prevAttendeeTypeId: a.attendeeTypeId,
      key: a.personId,
      ...shapeAttendee(a)
    }))
  )

  const allAttendeesFree =
    hideCost ||
    !attendees.some(attendee => {
      const attendeeType = attendeeTypes.find(
        attendeeType => attendeeType.id === attendee.attendeeTypeId
      )

      return attendeeType && isPresent(attendeeType.price_string)
    })

  const validate = newAttendees => {
    if (newAttendees.length === 0) return false

    const validAttendees = newAttendees.map(
      a => isPresent(a.attendeeTypeId) && isPresent(a.personId)
    )

    return !validAttendees.includes(false)
  }

  const handleAttendeeRemove = attendee => {
    const attendeeType = attendeeTypes.find(at => at.id === attendee.attendeeTypeId)

    const availableCapacity = attendeeType
      ? attendeeType.available_capacity === null
        ? Infinity
        : attendeeType.available_capacity
      : Infinity

    let typeCount = 0
    let hasReachedCapacity = false

    const newAttendees = attendees
      .filter(a => a.personId !== attendee.personId)
      .map(a => {
        if (attendeeType && attendeeType.id === a.attendeeTypeId) {
          typeCount += 1
          hasReachedCapacity = typeCount > availableCapacity
        } else {
          hasReachedCapacity = a.hasReachedCapacity
        }

        return {
          ...a,
          hasReachedCapacity
        }
      })

    setAttendees(newAttendees)
    handleAttendeesChange(newAttendees)
  }

  const handleAttendeesChange = newAttendees => {
    onAttendeesChange(newAttendees, { isValid: validate(newAttendees) })
  }

  const handleAttendeeSelect = async (accountCenterPerson, _downshift) => {
    if (!accountCenterPerson) {
      return
    }

    const { person } = await api.post(`${accountsPeoplePath}`, {
      person: { account_center_id: accountCenterPerson.account_center_id }
    })

    const attendee = attendees.find(a => a.key === "new")

    const newAttendee = {
      ...attendee,
      ...shapeAttendee(person),
      key: person.id,
      id: person.id,
      attendeeId: null,
      personId: person.id
    }

    updateAttendees(newAttendee)
  }

  const updateAttendees = (attendee, attendeeTypeId = null) => {
    let attendeeIdentifier = null

    if (attendeeTypeId === null || !attendee.personId) {
      attendeeIdentifier = "new"
    } else {
      attendeeIdentifier = attendee.personId
    }

    const newAttendeeType =
      attendeeTypeId !== null ? attendeeTypeId.toString() : attendee.attendeeTypeId.toString()

    const attendeeType = attendeeTypes.find(at => at.id.toString() === newAttendeeType)

    const previousAttendeeType = attendeeTypes.find(at => at.id === attendee.attendeeTypeId)

    const availableCapacity = attendeeType
      ? attendeeType.available_capacity === null
        ? Infinity
        : attendeeType.available_capacity
      : Infinity

    const previousAvailableCapacity = previousAttendeeType
      ? previousAttendeeType.available_capacity === null
        ? Infinity
        : previousAttendeeType.available_capacity
      : Infinity

    let hasReachedCapacity = false
    let prevCount = 0
    let newCount = 0

    const newAttendees = attendees.map(a => {
      if (a.key === attendeeIdentifier || (attendeeType && a.attendeeTypeId === attendeeType.id)) {
        newCount += 1
        hasReachedCapacity = newCount > availableCapacity
      } else if (previousAttendeeType && a.attendeeTypeId === previousAttendeeType.id) {
        prevCount += 1
        hasReachedCapacity = prevCount > previousAvailableCapacity
      } else {
        hasReachedCapacity = a.hasReachedCapacity
      }

      if (a.key === attendeeIdentifier) {
        return {
          ...a,
          ...attendee,
          attendeeTypeId: attendeeType ? attendeeType.id : "",
          hasReachedCapacity
        }
      } else {
        return {
          ...a,
          hasReachedCapacity
        }
      }
    })

    setAttendees(newAttendees)
    handleAttendeesChange(newAttendees)
  }

  const handleAttendeeTypeSelect = e => {
    const attendeeTypeId = Number(e.target.value)

    const newAttendees = [...attendees, { key: "new", attendeeTypeId }]

    setAttendees(newAttendees)
    handleAttendeesChange(newAttendees)
  }

  const handleAttendeeTypeChange = (e, attendee) => {
    updateAttendees(attendee, e.target.value)
  }

  const renderText = (text, isCanceled) => (
    <div className={`fs-3 ${isCanceled ? "t-strike c-neuatral-500" : ""}`}>{text}</div>
  )

  const renderAttendeeTypeSelect = row => {
    const attendeeType = attendeeTypes.find(at => at.id === row.attendeeTypeId)

    if (row.isCanceled) {
      return attendeeType ? renderText(attendeeType.name, row.isCanceled) : null
    }

    return (
      <Select
        name={`${row.id}-attendeeType`}
        value={`${row.attendeeTypeId.toString()}` || ""}
        className="select"
        options={[
          { value: "", label: "Select a selection" },
          ...attendeeTypes.map(at => ({
            value: at.id,
            label: at.nameWithRestriction
          }))
        ]}
        onChange={e => handleAttendeeTypeChange(e, row)}
      />
    )
  }

  const renderName = row => {
    return row.name ? (
      renderText(row.name, row.isCanceled)
    ) : (
      <AccountCenterSearch
        inputProps={{
          placeholder: "Attendee name"
        }}
        menuProps={{
          style: { zIndex: 1 }
        }}
        onSelect={handleAttendeeSelect}
        className="f-1"
      />
    )
  }

  const renderAttendeeTypeCost = row => {
    const attendeeType = attendeeTypes.find(at => at.id === row.attendeeTypeId)

    return attendeeType ? renderText(attendeeType.price_string, row.isCanceled) : null
  }

  const renderRemoveButton = row => {
    if (!row.key || row.attendeeId) {
      return null
    }

    return (
      <button
        className="c-p p-1 d-f ai-c"
        aria-label="Remove attendee"
        onClick={() => handleAttendeeRemove(row)}
      >
        <Icon symbol="general#minus-circle" className="c-danger" />
      </button>
    )
  }

  const checkWarningMessage = row => {
    const attendeeType = attendeeTypes.find(at => at.id === row.attendeeTypeId)

    let warningMessage = ""

    if (row.personId) {
      if (row.prevAttendeeTypeId && row.attendeeTypeId === row.prevAttendeeTypeId) {
        warningMessage = ""
      } else {
        warningMessage = checkRestrictionMessage(row, attendeeType)

        if (row.prevAttendeeTypeId && row.attendeeTypeId !== row.prevAttendeeTypeId) {
          warningMessage = `Changing ${row.firstName}'s selection may effect what forms, add-ons, discounts, scholarships, and assignments are available to them. ${warningMessage}`
        }
      }
    }

    return warningMessage ? (
      <div
        className="alert warning-alert mb-0 p-1 font-size-4"
        css={{ gridColumn: "span 3", marginTop: "-8px" }}
      >
        {warningMessage}
      </div>
    ) : null
  }

  const isNewAttendeeShowing = attendees.some(a => a.key === "new")

  return (
    <div
      css={{
        display: "grid",
        gridTemplateColumns: "max-content 1fr max-content",
        columnGap: "24px",
        rowGap: "16px",
        alignItems: "center"
      }}
    >
      {attendees.map(a => (
        <Fragment key={a.key}>
          <div className="d-f jc-fs g-1 ai-c">
            {attendees.length > existingAttendees.length && renderRemoveButton(a)}
            {renderName(a)}
          </div>
          <div
            css={{
              justifySelf: "end",
              width: "100%",
              marginRight: allAttendeesFree ? "-24px" : null
            }}
          >
            {renderAttendeeTypeSelect(a)}
          </div>
          <div css={{ justifySelf: "end" }}>{!hideCost && renderAttendeeTypeCost(a)}</div>
          {displayWarnings && checkWarningMessage(a)}
        </Fragment>
      ))}

      {!isNewAttendeeShowing && (
        <Select
          name="new-attendeeType"
          className="select"
          css={{ gridColumn: "span 3" }}
          value=""
          options={[
            { value: "", label: "Add selection" },
            ...attendeeTypes.map(at => ({
              value: at.id,
              label: at.nameWithRestriction
            }))
          ]}
          onChange={handleAttendeeTypeSelect}
        />
      )}
    </div>
  )
}

AddAttendees.propTypes = {
  accountsPeoplePath: PropTypes.string.isRequired,
  onAttendeesChange: PropTypes.func.isRequired,
  attendeeTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    })
  ).isRequired,
  existingAttendees: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      personId: PropTypes.number,
      attendeeTypeId: PropTypes.number
    })
  ),
  displayWarnings: PropTypes.bool,
  hideCost: PropTypes.bool
}

export default AddAttendees
